import { logger } from "../tslog";
import { SlideShapeInfo } from "../../types/contentFraming";

// Function to detect slide title based on criteria
export function detectSlideTitle(shapes: SlideShapeInfo[], slideDimensions: {height: number, width: number}): SlideShapeInfo | null {
  const { height: slideHeight } = slideDimensions;
  const top20Percent = slideHeight * 0.2;

  // Filter shapes that are in the top 20% of the slide
  const topShapes = shapes.filter(shape => shape.top <= top20Percent);

  if (topShapes.length === 0) {
    logger.info("No shapes found in top 20% of slide");
    return null;
  }

  // Find the widest shape among top shapes
  const widestShape = topShapes.reduce((widest, current) => {
    return current.width > widest.width ? current : widest;
  });

  // Check if the widest shape is at least 80% of other shapes' width
  const otherShapes = shapes.filter(shape => shape.id !== widestShape.id);
  const averageWidth = otherShapes.length > 0 ? 
    otherShapes.reduce((sum, shape) => sum + shape.width, 0) / otherShapes.length : 0;

  const isWideEnough = otherShapes.length === 0 || widestShape.width >= (averageWidth * 0.8);

  // Check if shape name suggests it's a title
  const nameSuggestsTitle = widestShape.name.toLowerCase().includes('title') || 
                           widestShape.name.toLowerCase().includes('heading') ||
                           widestShape.name.toLowerCase().includes('header');

  if (isWideEnough || nameSuggestsTitle) {
    logger.info("Slide title detected:", {
      id: widestShape.id,
      name: widestShape.name,
      width: widestShape.width,
      top: widestShape.top,
      isWideEnough,
      nameSuggestsTitle
    });
    return widestShape;
  }

  logger.info("No slide title detected");
  return null;
}

// Function to detect slide subtitle based on criteria
export function detectSlideSubtitle(shapes: SlideShapeInfo[], slideTitle: SlideShapeInfo | null): SlideShapeInfo | null {
  if (!slideTitle) {
    logger.info("No slide title found, cannot detect subtitle");
    return null;
  }

  // Find shapes positioned below the slide title
  const shapesBelowTitle = shapes.filter(shape => 
    shape.id !== slideTitle.id && 
    shape.top > slideTitle.top
  );

  if (shapesBelowTitle.length === 0) {
    logger.info("No shapes found below slide title");
    return null;
  }

  // Find the shape closest to the slide title (within 30 pixels)
  const closeShapes = shapesBelowTitle.filter(shape => 
    Math.abs(shape.top - (slideTitle.top + slideTitle.height)) <= 30
  );

  if (closeShapes.length === 0) {
    logger.info("No shapes found within 30 pixels of slide title");
    return null;
  }

  // Find the shape with the most overlap with the title
  let bestSubtitle: SlideShapeInfo | null = null;
  let maxOverlap = 0;

  for (const shape of closeShapes) {
    // Check if left position is close to title's left position
    const leftPositionMatch = Math.abs(shape.left - slideTitle.left) <= 10;
    
    // Calculate width overlap percentage
    const overlapStart = Math.max(shape.left, slideTitle.left);
    const overlapEnd = Math.min(shape.left + shape.width, slideTitle.left + slideTitle.width);
    const overlapWidth = Math.max(0, overlapEnd - overlapStart);
    const overlapPercentage = overlapWidth / Math.min(shape.width, slideTitle.width);

    if (leftPositionMatch && overlapPercentage >= 0.7) {
      if (overlapPercentage > maxOverlap) {
        maxOverlap = overlapPercentage;
        bestSubtitle = shape;
      }
    }
  }

  if (bestSubtitle) {
    logger.info("Slide subtitle detected:", {
      id: bestSubtitle.id,
      name: bestSubtitle.name,
      overlapPercentage: maxOverlap,
      top: bestSubtitle.top,
      left: bestSubtitle.left
    });
  } else {
    logger.info("No slide subtitle detected");
  }

  return bestSubtitle;
}

// Function to detect source shapes
export function isSourceShape(shape: SlideShapeInfo): boolean {
  const text = (shape.text || "").trim().toLowerCase();
  const name = (shape.name || "").toLowerCase();
  
  // Check if text starts with "Source:" or "Source"
  const startsWithSource = /^\s*source\s*:?\s*/i.test(text);
  
  // Check if name suggests it's a source
  const nameSuggestsSource = name.includes('source') || 
                           name.includes('citation') ||
                           name.includes('reference');
  
  // Check if it's positioned at the bottom of the slide (typical for sources)
  const isBottomPositioned = shape.top > 400; // Adjust threshold as needed
  
  return startsWithSource || (nameSuggestsSource && isBottomPositioned);
}

// Function to detect notes shapes
export function isNotesShape(shape: SlideShapeInfo): boolean {
  const text = (shape.text || "").trim().toLowerCase();
  const name = (shape.name || "").toLowerCase();
  
  // Check if text starts with "Notes:" or "Notes"
  const startsWithNotes = /^\s*notes\s*:?\s*/i.test(text);
  
  // Check if name suggests it's notes
  const nameSuggestsNotes = name.includes('notes') || 
                           name.includes('note') ||
                           name.includes('annotation');
  
  // Check if it contains numbered list pattern (typical for notes)
  const hasNumberedList = /^\s*\d+\s*\.\s*/m.test(text);
  
  // Check if it's positioned at the bottom of the slide (typical for notes)
  const isBottomPositioned = shape.top > 400; // Adjust threshold as needed
  
  return startsWithNotes || (nameSuggestsNotes && isBottomPositioned) || (hasNumberedList && isBottomPositioned);
}

// Function to detect source and notes using target shape as reference
export async function detectSourceAndNotes(targetShape: any, shapes: SlideShapeInfo[], slideDimensions: {height: number, width: number}): Promise<{source: SlideShapeInfo | null, notes: SlideShapeInfo | null}> {
  try {
    if (!targetShape) {
      logger.info("No target shape provided for source and notes detection");
      return {
        source: null,
        notes: null
      };
    }

    logger.info("Detecting source and notes using target shape as reference", {
      targetShapeId: targetShape.id,
      targetShapeName: targetShape.name,
      targetShapePosition: { left: targetShape.left, top: targetShape.top, width: targetShape.width, height: targetShape.height }
    });

    const targetLeft = targetShape.left;
    const targetTop = targetShape.top;
    const targetWidth = targetShape.width;
    const targetHeight = targetShape.height;
    const targetRight = targetLeft + targetWidth;
    const targetBottom = targetTop + targetHeight;

    // Define search region below target shape
    const searchRegionTop = targetBottom; // Bottom edge of target
    const maxGapBelowTarget = 15; // Only consider shapes within 15px below target
    const searchRegionBottom = Math.min(targetBottom + maxGapBelowTarget, slideDimensions.height * 0.95);
    const horizontalOverlapThreshold = 0.4; // 40% horizontal overlap required

    // Helper function to check horizontal overlap
    const hasHorizontalOverlap = (shapeLeft: number, shapeRight: number) => {
      const overlapLeft = Math.max(targetLeft, shapeLeft);
      const overlapRight = Math.min(targetRight, shapeRight);
      const overlapWidth = Math.max(0, overlapRight - overlapLeft);
      const minWidth = Math.min(targetWidth, shapeRight - shapeLeft);
      return overlapWidth / minWidth >= horizontalOverlapThreshold;
    };

    // Helper to filter out slide-number-like shapes (e.g., footer page numbers)
    const isSlideNumberLike = (shape: any) => {
      const name = String(shape.name || '').toLowerCase();
      const label = String(shape.label || '');
      const text = String(shape.text || '');
      const isNumericOnly = /^\s*\d+\s*$/.test(text);
      return name.includes('slide number') || label === '#' || isNumericOnly;
    };

    // Find shapes below target shape with horizontal overlap
    const shapesBelow = shapes
      .filter(shape => shape.id !== targetShape.id)
      .filter(shape => !isSlideNumberLike(shape))
      .filter(shape => {
        const shapeTop = shape.top;
        const shapeLeft = shape.left;
        const shapeRight = shape.left + shape.width;

        const verticalGapFromTarget = shapeTop - targetBottom;
        const withinGap = verticalGapFromTarget >= 0 && verticalGapFromTarget <= maxGapBelowTarget;
        const hasOverlap = hasHorizontalOverlap(shapeLeft, shapeRight);

        return withinGap && hasOverlap;
      })
      .sort((a, b) => a.top - b.top); // Sort by top position (closest to target first)

    logger.info("Shapes below target shape", {
      targetShapeId: targetShape.id,
      searchRegionTop,
      searchRegionBottom,
      shapesBelow: shapesBelow.map(s => ({
        id: s.id,
        name: s.name,
        top: s.top,
        left: s.left,
        width: s.width,
        height: s.height,
        text: s.text?.substring(0, 50) + "..."
      }))
    });

    let sourceShape: SlideShapeInfo | null = null;
    let notesShape: SlideShapeInfo | null = null;

    if (shapesBelow.length === 0) {
      logger.info("No shapes found below target shape");
    }

    if (shapesBelow.length === 1) {
      // Only one shape below - check if it's source or notes
      const shape = shapesBelow[0];
      if (isSourceShape(shape)) {
        sourceShape = shape;
      } else if (isNotesShape(shape)) {
        notesShape = shape;
      } else {
        // Default to source if it's the only shape below
        sourceShape = shape;
      }
    } else if (shapesBelow.length >= 2) {
      // Multiple shapes below - first is source, second is notes
      sourceShape = shapesBelow[0];
      
      // Find the nearest shape below the source for notes
      const sourceBottom = sourceShape.top + sourceShape.height;
      const notesCandidate = shapesBelow
        .slice(1) // Skip the first shape (source)
        .find(shape => {
          const gap = shape.top - sourceBottom;
          return gap <= 15; // Within 15px of source
        });

      if (notesCandidate) {
        notesShape = notesCandidate;
      } else {
        // If no close notes candidate, use the second shape
        notesShape = shapesBelow[1];
      }
    }

    // Fallback: If no shapes below target, check bottom area of slide
    if ((!sourceShape && !notesShape) || (sourceShape === null && notesShape === null)) {
      const bottomArea = slideDimensions.height * 0.8; // Bottom 20% of slide
      const bottomShapes = shapes
        .filter(shape => shape.id !== targetShape.id)
        .filter(shape => !isSlideNumberLike(shape))
        .filter(shape => shape.top >= bottomArea)
        .sort((a, b) => b.top - a.top); // Sort by bottom position (lowest first)

      logger.info("Bottom shapes", {
        bottomShapes: bottomShapes.map(s => ({
          id: s.id,
          name: s.name,
          top: s.top,
          left: s.left,
          width: s.width,
          height: s.height,
          text: s.text?.substring(0, 50) + "..."
        }))
      });

      if (bottomShapes.length >= 2) {
        // Lowest shape is notes, second lowest is source
        notesShape = bottomShapes[0];
        sourceShape = bottomShapes[1];
      } else if (bottomShapes.length === 1) {
        // Only one shape in bottom area - check content to determine
        const shape = bottomShapes[0];
       notesShape = shape;
        
      }
    }

    logger.info("Source and notes detection results", {
      sourceShape: sourceShape ? {
        id: sourceShape.id,
        name: sourceShape.name,
        top: sourceShape.top,
        text: sourceShape.text?.substring(0, 50) + "..."
      } : null,
      notesShape: notesShape ? {
        id: notesShape.id,
        name: notesShape.name,
        top: notesShape.top,
        text: notesShape.text?.substring(0, 50) + "..."
      } : null
    });

    return {
      source: sourceShape,
      notes: notesShape
    };

  } catch (error) {
    logger.error("Error in detectSourceAndNotes:", error);
    return {
      source: null,
      notes: null
    };
  }
}

// Function to detect placeholder title and subtitle using target shape as reference
export async function detectPlaceholderTitleAndSubtitle(targetShape: any, shapes: SlideShapeInfo[], slideDimensions: {height: number, width: number}): Promise<{title: SlideShapeInfo | null, subTitle: SlideShapeInfo | null}> {
  try {
    if (!targetShape) {
      logger.info("No target shape provided, using fallback detection");
      return {
        title: detectSlideTitle(shapes, slideDimensions),
        subTitle: null
      };
    }

    logger.info("Detecting placeholder title and subtitle using target shape as reference", {
      targetShapeId: targetShape.id,
      targetShapeName: targetShape.name,
      targetShapePosition: { left: targetShape.left, top: targetShape.top, width: targetShape.width, height: targetShape.height }
    });

    const contentLeft = targetShape.left;
    const contentTop = targetShape.top;
    const contentWidth = targetShape.width;
    const contentRight = contentLeft + contentWidth;

    // Define a horizontal search strip directly above the target shape
    const regionPadPx = 20; // extend region horizontally by ±20 px
    const regionHeightPx = 30; // vertical strip height
    const horizOverlapThreshold = 0.6; // fallback threshold
    const maxEdgeCenterTolPx = 40; // for alignment normalization

    // - Left/Right: same as target shape, extended slightly (±regionPadPx)
    // - Top: contentTop - regionHeightPx
    // - Bottom: contentTop
    const regionLeft = contentLeft - regionPadPx;
    const regionRight = contentRight + regionPadPx;
    const regionTop = contentTop - regionHeightPx;
    const regionBottom = contentTop;

    const widthTolerance = 0.15; // ±15%
    const horizontalGapTolerancePx = 20; // allow small horizontal misalignment for edges/center

    // Helper computations
    const computeHorizontalOverlapFraction = (aLeft: number, aRight: number, bLeft: number, bRight: number) => {
      const overlap = Math.min(aRight, bRight) - Math.max(aLeft, bLeft);
      if (overlap <= 0) return 0;
      const minWidth = Math.max(1, Math.min(aRight - aLeft, bRight - bLeft));
      return overlap / minWidth;
    };

    const isWidthSimilar = (w: number, ref: number) => {
      if (ref <= 0 || w <= 0) return false;
      const absDiff = Math.abs(w - ref);
      const relDiffRef = absDiff / ref;
      const relDiffMax = absDiff / Math.max(w, ref);
      const absPxTolerance = 8; // allow small absolute variance
      return relDiffRef <= widthTolerance || relDiffMax <= widthTolerance || absDiff <= absPxTolerance;
    };

    // Analyze all shapes for title/subtitle candidates
    const analyzed = shapes
      .filter(shape => shape.id !== targetShape.id)
      .map(shape => {
        const left = shape.left;
        const top = shape.top;
        const width = shape.width;
        const height = shape.height;
        const right = left + width;
        const bottom = top + height;
        const horizOverlap = computeHorizontalOverlapFraction(left, right, contentLeft, contentRight);
        const above = bottom <= contentTop;
        const verticalGap = contentTop - bottom;
        const name = shape.name;
        const absDiff = Math.abs(width - contentWidth);
        const computedWidthSimilar = isWidthSimilar(width, contentWidth);
        const centerX = left + width / 2;
        const contentCenterX = contentLeft + contentWidth / 2;
        const leftGapAbs = Math.abs(left - contentLeft);
        const centerGapAbs = Math.abs(centerX - contentCenterX);

        return {
          shape,
          left,
          top,
          width,
          height,
          bottom,
          horizOverlap,
          widthSimilar: computedWidthSimilar,
          above,
          verticalGap,
          name,
          absDiff,
          leftGapAbs,
          centerGapAbs,
        };
      });

    logger.info("Analyzed shapes above target shape", {
      referenceWidth: contentWidth,
      items: analyzed
        .filter(c => c.above)
        .map(c => ({
          id: c.shape.id,
          name: c.name,
          left: c.left,
          top: c.top,
          width: c.width,
          height: c.height,
          bottom: c.bottom,
          gapToTargetTop: c.verticalGap,
          widthSimilar: c.widthSimilar,
          absDiff: c.absDiff,
          horizOverlap: c.horizOverlap,
          leftGapAbs: c.leftGapAbs,
          centerGapAbs: c.centerGapAbs,
        })),
    });

    // PRIORITY: Shapes with ≥80% horizontal overlap - ignore all other conditions
    const highOverlapThreshold = 0.8; // 80% overlap
    const highOverlapCandidates = analyzed.filter(c => c.above && c.horizOverlap >= highOverlapThreshold);
    
    logger.info("High overlap candidates (≥80% - priority, ignoring other conditions)", {
      candidateCount: highOverlapCandidates.length,
      threshold: highOverlapThreshold,
      items: highOverlapCandidates.map(c => ({
        id: c.shape.id,
        name: c.name,
        overlap: c.horizOverlap,
        gap: c.verticalGap,
        width: c.width,
        widthSimilar: c.widthSimilar,
        leftGapAbs: c.leftGapAbs,
      })),
    });

    // First pass: strictly width-similar shapes above the target with left edge aligned within tolerance
    // Only if no high-overlap candidates exist
    let candidates: typeof analyzed = [];
    
    if (highOverlapCandidates.length > 0) {
      // Use high-overlap candidates, ignore other conditions
      candidates = highOverlapCandidates;
      logger.info("Using high-overlap candidates (≥80%), ignoring width/alignment conditions");
    } else {
      // Fallback to original logic: width-similar and left-aligned
      candidates = analyzed.filter(c => c.above && c.widthSimilar && c.leftGapAbs <= horizontalGapTolerancePx);

      logger.info("Width-similar candidates (preferred: left-edge aligned)", {
        candidateCount: candidates.length,
        referenceWidth: contentWidth,
        tolerancePct: widthTolerance,
        items: candidates.map(c => ({
          id: c.shape.id,
          name: c.name,
          width: c.width,
          absDiff: c.absDiff,
          gap: c.verticalGap,
          leftGapAbs: c.leftGapAbs,
          leftGapTolerancePx: horizontalGapTolerancePx,
        })),
      });

      // Fallback: allow strong horizontal overlap if no width-similar candidates
      if (candidates.length === 0) {
        candidates = analyzed.filter(c => c.above && c.horizOverlap >= horizOverlapThreshold);
        logger.info("Fallback to overlap-based candidates", {
          candidateCount: candidates.length,
          items: candidates.map(c => ({ id: c.shape.id, name: c.name, overlap: c.horizOverlap, gap: c.verticalGap })),
        });
      }
    }

    // Title candidates: any candidate whose bottom edge lies within the search region strip
    const titleCandidates = candidates.filter(c => c.bottom >= regionTop && c.bottom <= regionBottom);

    logger.info("Title candidate gaps", {
      items: titleCandidates.map(c => ({
        id: c.shape.id,
        name: c.name,
        gap: c.verticalGap,
        bottom: c.bottom,
      })),
    });

    // Scoring weights
    const weightVertical = 0.45;
    const weightHorizontal = 0.25;
    const weightOverlap = 0.15;
    const bonusPlaceholder = 0.15;
    const bonusTextLike = 0.05;
    const bonusEmpty = 0.1;

    const computeHorizontalScore = (absDiff: number, leftGapAbs: number, centerGapAbs: number) => {
      const widthPenalty = Math.min(1, (absDiff || 0) / Math.max(1, contentWidth));
      const edgePenalty = Math.min(1, (leftGapAbs || 0) / maxEdgeCenterTolPx);
      const centerPenalty = Math.min(1, (centerGapAbs || 0) / maxEdgeCenterTolPx);
      return Math.max(0, 1 - (0.5 * widthPenalty + 0.25 * edgePenalty + 0.25 * centerPenalty));
    };

    // Compute candidate scores
    type Scored = (typeof analyzed)[0] & { score: number; text?: string };
    
    const scored: Scored[] = titleCandidates.map(c => {
      let textContent = "";
      try {
        textContent = String(c.shape.text || "");
      } catch (_) {}

      // Vertical proximity: smaller gap -> higher score (normalize to 0..1 across 0..regionHeightPx)
      const verticalGapPx = Math.max(0, Math.min(regionHeightPx, c.verticalGap));
      const verticalScore = 1 - verticalGapPx / regionHeightPx;

      // Horizontal alignment: width and edge/center alignment closeness
      const horizontalScore = computeHorizontalScore(c.absDiff, c.leftGapAbs, c.centerGapAbs);

      // Overlap factor to reward strong horizontal covering
      const overlapScore = Math.max(0, Math.min(1, c.horizOverlap || 0));

      // Placeholder vs text box bonus
      const nameLower = (c.name || "").toLowerCase();
      const isPlaceholder = nameLower.includes("placeholder") || /\b(title|subtitle)\b/.test(nameLower);
      const placeholderBonus = isPlaceholder ? bonusPlaceholder : 0;

      // Text-like soft bonus
      const looksLikeText = nameLower.includes("text");
      const textLikeBonus = looksLikeText ? bonusTextLike : 0;

      // Empty/boilerplate text bonus
      const textLower = textContent.trim().toLowerCase();
      const isEmptyOrBoiler = textLower === "" || textLower.includes("click to add text");
      const emptyBonus = isEmptyOrBoiler ? bonusEmpty : 0;

      // Composite score (weights can be tuned)
      const score =
        weightVertical * verticalScore +
        weightHorizontal * horizontalScore +
        weightOverlap * overlapScore +
        placeholderBonus +
        emptyBonus +
        textLikeBonus;

      return { ...c, score, text: textContent } as Scored;
    });

    // Sort with deterministic tie-breakers
    scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if ((b.horizOverlap || 0) !== (a.horizOverlap || 0)) return (b.horizOverlap || 0) - (a.horizOverlap || 0);
      if ((a.verticalGap || 0) !== (b.verticalGap || 0)) return (a.verticalGap || 0) - (b.verticalGap || 0); // closer is better
      if ((a.centerGapAbs || 0) !== (b.centerGapAbs || 0)) return (a.centerGapAbs || 0) - (b.centerGapAbs || 0);
      return String(a.shape.id).localeCompare(String(b.shape.id));
    });

    // Post-sort filter: exclude oversized candidates (height > 50px)
    const scoredFiltered = scored.filter(c => Number(c.height) <= 50);

    logger.info("Scored title candidates", {
      items: scoredFiltered.map(c => ({
        id: c.shape.id,
        name: c.name,
        score: Number(c.score.toFixed(3)),
        gap: c.verticalGap,
        leftGap: c.leftGapAbs,
        centerGap: c.centerGapAbs,
        widthAbsDiff: c.absDiff,
        placeholderHint: (c.name || "").toLowerCase().includes("placeholder"),
        emptyOrBoiler: c.text ? c.text.trim() === "" || c.text.toLowerCase().includes("click to add text") : false,
        overlap: c.horizOverlap,
      })),
    });

    // Select final title/subTitle per requested rule:
    // - If 2+ shapes: highest score -> subTitle, second highest -> title
    // - If 1 shape: treat as title
    // - If 0: none
    let likelyTitle: any = null;
    let likelySubTitle: any = null;
    if (scoredFiltered.length >= 2) {
      likelySubTitle = scoredFiltered[0];
      likelyTitle = scoredFiltered[1];
    } else if (scoredFiltered.length === 1) {
      likelyTitle = scoredFiltered[0];
    }

    logger.info("Placeholder title and subtitle detection results", {
      likelyTitle: likelyTitle ? {
        id: likelyTitle.shape.id,
        name: likelyTitle.shape.name,
        score: likelyTitle.score
      } : null,
      likelySubTitle: likelySubTitle ? {
        id: likelySubTitle.shape.id,
        name: likelySubTitle.shape.name,
        score: likelySubTitle.score
      } : null,
    });

    return {
      title: likelyTitle ? likelyTitle.shape : null,
      subTitle: likelySubTitle ? likelySubTitle.shape : null
    };

  } catch (error) {
    logger.error("Error in detectPlaceholderTitleAndSubtitle:", error);
    return {
      title: null,
      subTitle: null
    };
  }
}

// Function to detect key take-aways shapes
export function detectKeyTakeAways(shape: SlideShapeInfo, slideDimensions: {height: number, width: number}): boolean {
  const { height: slideHeight, width: slideWidth } = slideDimensions;
  
  // Check if shape is positioned on the far left of the page
  const leftSide = slideWidth * 0.3; // Within 30% of slide width from left
  const isOnLeftSide = shape.left <= leftSide;
  
  // Check if shape has narrow width (less than 30% of page width)
  const maxWidth = slideWidth * 0.3;
  const isNarrowWidth = shape.width <= maxWidth;
  
  // Check if shape has substantial height (at least 30% of page height)
  const minHeight = slideHeight * 0.3;
  const isSubstantialHeight = shape.height >= minHeight;
  
  // Check text content for key take-aways indicators
  const text = (shape.text || "").trim().toLowerCase();
  const name = (shape.name || "").toLowerCase();

  // Check if name contains "content placeholder" - this is a strong indicator for Key Take-Aways
  // Even if text is empty, Content Placeholder shapes on the left side are likely Key Take-Aways
  const isContentPlaceholder = name.includes('content placeholder');
  
  // Check if text contains key take-aways keywords
  const hasKeyTakeAwaysKeywords = /key\s*take|takeaway|take\s*away|key\s*point|summary|highlight/i.test(text);
  
  // Check if text contains numbered or bulleted list (common in key take-aways)
  const hasListPattern = /^\s*[\d\-\*\+]\s+/m.test(text) || 
                        /^\s*\d+\.\s+/m.test(text) ||
                        /^\s*[•·]\s+/m.test(text);
  
  // Check if text is substantial (not just a few words)
  const hasSubstantialText = text.length > 20;
  
  // Position and size criteria must be met
  const meetsPositionCriteria = isOnLeftSide && isNarrowWidth && isSubstantialHeight;
  
  // Content criteria (at least one must be met)
  // Content Placeholder on left side is sufficient even with empty text
  const meetsContentCriteria = 
                              isContentPlaceholder || 
                              hasKeyTakeAwaysKeywords ||
                              hasListPattern || 
                              hasSubstantialText;
  
  const isKeyTakeAways = meetsPositionCriteria && meetsContentCriteria;
  
  if (isKeyTakeAways) {
    logger.info("Key Take-Aways shape detected:", {
      id: shape.id,
      name: shape.name,
      position: { left: shape.left, top: shape.top },
      dimensions: { width: shape.width, height: shape.height },
      textPreview: text.substring(0, 50) + "...",
      criteria: {
        isOnLeftSide,
        isNarrowWidth,
        isSubstantialHeight,
        isContentPlaceholder,
      }
    });
  }
  
  return isKeyTakeAways;
}

// Function to categorize other shapes based on position and content
export function categorizeShape(shape: SlideShapeInfo, slideTitle: SlideShapeInfo | null, slideDimensions: {height: number, width: number}): string {
  const { height: slideHeight, width: slideWidth } = slideDimensions;
  
  // Check if shape is at the bottom of the slide (footer area)
  const bottomArea = slideHeight * 0.9;
  if (shape.top >= bottomArea) {
    return "Footer";
  }

  // Check if shape is on the left side (sidebar)
  const leftSide = slideWidth * 0.3;
  if (shape.left <= leftSide && shape.width <= slideWidth * 0.4) {
    return "Sidebar";
  }

  // Check if shape is at the top but not the slide title
  const topArea = slideHeight * 0.2;
  if (shape.top <= topArea && shape.id !== slideTitle?.id) {
    return "Header";
  }

  // Check if shape has substantial text content
  if (shape.text && shape.text.trim().length > 20) {
    return "Content Text";
  }

  // Check if shape is a graphic/image
  if (shape.name.toLowerCase().includes('graphic') || 
      shape.name.toLowerCase().includes('image') ||
      shape.name.toLowerCase().includes('picture')) {
    return "Image/Graphic";
  }

  // Check if shape is small and might be a callout
  if (shape.width <= slideWidth * 0.2 && shape.height <= slideHeight * 0.1) {
    return "Callout";
  }

  // Default categorization
  return "Content";
}
